import React from "react";
import { Check, Lock, DollarSign, Brain, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { Reveal } from "../components/Reveal";
import { VideoEmbed } from "../components/VideoEmbed";
import { CtaBand } from "./Home";
import { ASSETS, CAPABILITIES, DIFFERENTIATORS, PERSONAS } from "../data/content";

const Overline = ({ children }) => (
  <span className="font-mono-el text-xs tracking-[0.25em] uppercase text-[#4FC3F7]">{children}</span>
);

const PILLARS = [
  {
    icon: Lock,
    title: "How agents solve lock-in",
    points: [
      "Every table lives in an open format, readable by any engine — with or without Apeiron.",
      "Bring-your-own storage: data stays in your own cloud account and remains intact if you leave.",
      "An open REST catalog means any compatible tool can discover and query your tables.",
    ],
  },
  {
    icon: DollarSign,
    title: "How agents solve cost opacity",
    points: [
      "Agent-driven provisioning spins up serverless compute per workload and terminates it — no idle billing.",
      "Every operation is tagged at execution time for real-time cost attribution — no month-end surprise.",
      "Bring-your-own storage means zero storage markup — your data, your cloud rates.",
    ],
  },
  {
    icon: Brain,
    title: "How agents solve shallow AI",
    points: [
      "An agent-native control plane exposes every capability as an addressable API — no human UI required.",
      "Git-style table branching lets agents experiment safely and merge back only when checks pass.",
      "Marketplace agents write predictions back as first-class data — closing the feedback loop.",
    ],
  },
];

export default function Product() {
  return (
    <div data-testid="product-page">
      {/* HERO */}
      <section className="relative pt-40 pb-24 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img src={ASSETS.productAbstract} alt="" className="w-full h-full object-cover opacity-30" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#020817] via-[#020817]/90 to-[#020817]" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12">
          <Reveal>
            <Overline>Apeiron · By EtherLake</Overline>
            <h1 className="mt-6 text-4xl md:text-5xl lg:text-6xl font-light leading-tight tracking-tight text-white max-w-4xl">
              The agent-first lakehouse —{" "}
              <span className="gradient-text font-medium">boundless by name, open by design.</span>
            </h1>
            <p className="mt-8 text-lg md:text-xl text-[#94A3B8] max-w-3xl leading-relaxed">
              Apeiron is an agent-first, semi-managed data lakehouse built entirely on open standards
              and open-source tooling. At its core is an intent-driven architecture: you describe what
              you want, and AI agents handle orchestration, ETL, analytics, and activation end to end —
              while your data stays in open formats in your own cloud.
            </p>
          </Reveal>
        </div>
      </section>

      {/* WALKTHROUGH VIDEO */}
      <section className="relative py-20 md:py-24 border-t border-white/10 bg-[#0A1A2F]/30">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <Reveal className="max-w-2xl">
            <Overline>A Guided Walkthrough</Overline>
            <h2 className="mt-5 text-3xl md:text-4xl font-light tracking-tight text-white">
              See how Apeiron works, <span className="gradient-text">end to end.</span>
            </h2>
            <p className="mt-6 text-[#94A3B8] text-lg leading-relaxed">
              A closer look at the platform in action — from stated intent to orchestrated
              pipelines, open tables, and governed access across every persona.
            </p>
          </Reveal>
          <Reveal delay={120} className="mt-12">
            <VideoEmbed videoId="mKRHcwAxB7A" title="Apeiron platform walkthrough" />
          </Reveal>
        </div>
      </section>

      {/* CAPABILITIES */}
      <section className="relative py-24 border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal>
            <Overline>What It Does</Overline>
            <h2 className="mt-5 text-3xl md:text-4xl lg:text-5xl font-light tracking-tight text-white max-w-3xl">
              One platform from raw event to activated insight.
            </h2>
          </Reveal>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-px bg-white/10 border border-white/10">
            {CAPABILITIES.map((c, i) => (
              <Reveal
                key={c.title}
                delay={(i % 2) * 80}
                className="bg-[#020817] p-8 md:p-10 hover:bg-[#0A1A2F]/60 transition-colors duration-300"
              >
                <div className="flex items-start gap-4">
                  <div className="mt-1 shrink-0 w-6 h-6 flex items-center justify-center bg-[#4FC3F7]/10 border border-[#4FC3F7]/30">
                    <Check className="w-3.5 h-3.5 text-[#4FC3F7]" strokeWidth={2.5} />
                  </div>
                  <div>
                    <h3 className="text-lg font-medium text-white">{c.title}</h3>
                    <p className="mt-2 text-[#94A3B8] text-sm leading-relaxed">{c.body}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* HOW AGENTS SOLVE */}
      <section className="relative py-24 md:py-32 bg-[#0A1A2F]/30 border-y border-white/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal>
            <Overline>The Solution</Overline>
            <h2 className="mt-5 text-3xl md:text-4xl lg:text-5xl font-light tracking-tight text-white max-w-3xl">
              AI doesn't just query the platform. <span className="gradient-text">AI runs it.</span>
            </h2>
          </Reveal>
          <div className="mt-16 grid grid-cols-1 lg:grid-cols-3 gap-6">
            {PILLARS.map((p, i) => {
              const Icon = p.icon;
              return (
                <Reveal
                  key={p.title}
                  delay={i * 120}
                  className="bg-white/5 border border-white/10 p-8 md:p-10 hover:border-[#4FC3F7]/30 transition-colors duration-300"
                >
                  <div className="w-12 h-12 flex items-center justify-center bg-[#4FC3F7]/10 border border-[#4FC3F7]/20 text-[#4FC3F7]">
                    <Icon className="w-6 h-6" strokeWidth={1.5} />
                  </div>
                  <h3 className="mt-6 text-xl font-medium text-white">{p.title}</h3>
                  <ul className="mt-6 space-y-4">
                    {p.points.map((pt) => (
                      <li key={pt} className="flex gap-3 text-sm text-[#94A3B8] leading-relaxed">
                        <span className="mt-2 shrink-0 w-1.5 h-1.5 bg-[#4FC3F7] rounded-full" />
                        {pt}
                      </li>
                    ))}
                  </ul>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* DIFFERENTIATORS TABLE */}
      <section className="relative py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal>
            <Overline>Why It's Different</Overline>
            <h2 className="mt-5 text-3xl md:text-4xl lg:text-5xl font-light tracking-tight text-white max-w-3xl">
              Every differentiator, and why it matters.
            </h2>
          </Reveal>
          <div className="mt-14 border border-white/10 divide-y divide-white/10">
            {DIFFERENTIATORS.map((d, i) => (
              <Reveal
                key={d.title}
                delay={(i % 3) * 60}
                className="grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-8 p-6 md:p-8 hover:bg-white/5 transition-colors duration-300"
              >
                <div className="md:col-span-4 flex items-center gap-3">
                  <span className="font-mono-el text-xs text-[#4FC3F7]">0{i + 1}</span>
                  <h3 className="text-lg font-medium text-white">{d.title}</h3>
                </div>
                <p className="md:col-span-8 text-[#94A3B8] text-sm md:text-base leading-relaxed">
                  {d.body}
                </p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* WHO IT'S FOR */}
      <section className="relative py-24 md:py-32 bg-[#0A1A2F]/30 border-y border-white/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal>
            <Overline>Who Should Use Apeiron</Overline>
            <h2 className="mt-5 text-3xl md:text-4xl lg:text-5xl font-light tracking-tight text-white max-w-3xl">
              Purpose-built for the underserved middle.
            </h2>
            <p className="mt-6 text-[#94A3B8] text-lg max-w-2xl leading-relaxed">
              Any enterprise can run Apeiron — but it's sized for teams that need warehouse-grade
              analytics and governance without warehouse-grade cost or headcount.
            </p>
          </Reveal>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-6">
            {PERSONAS.map((p, i) => (
              <Reveal
                key={p.role}
                delay={(i % 2) * 100}
                className="bg-white/5 border border-white/10 p-8 hover:border-[#4FC3F7]/30 transition-colors duration-300"
              >
                <h3 className="text-lg font-medium text-white">{p.role}</h3>
                <p className="mt-3 text-[#94A3B8] text-sm leading-relaxed">{p.body}</p>
              </Reveal>
            ))}
          </div>
          <Reveal delay={120}>
            <Link
              to="/use-cases"
              data-testid="product-usecases-link"
              className="mt-12 group inline-flex items-center gap-2 text-[#4FC3F7] hover:text-[#00E5FF] transition-colors"
            >
              See agents in action
              <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
            </Link>
          </Reveal>
        </div>
      </section>

      <CtaBand />
    </div>
  );
}
